import React, { useState } from 'react';
import { EnrollmentPeriod, InstalledProductLine, InstalledRate, ProductType } from 'types/enrollmentService';
import { convertRateValue } from 'global/utils/convertRateValue';
import clsx from 'clsx';
import {
  Add,
  Download,
  InfoButton,
  NavigationClose,
  Reload,
  Search,
} from '@insurancemenu/imenu-components-lib/dist/icons';
import { Button, Tooltip } from '@insurancemenu/imenu-components-lib/dist/components';
import colors from '@insurancemenu/imenu-components-lib/dist/utils/colors';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { observer } from 'mobx-react-lite';
import { useRootStore } from 'store/rootStore';
import { employerRights } from '@insurancemenu/imenu-components-lib/dist/utils/accessRights';
import { toJS } from 'mobx';
import { getUploadExhibitZipUrl } from 'api/operations/carrierFileService';
import { downloadByUrl } from '@insurancemenu/imenu-components-lib/dist/helpers/downloadByUrl';
import { validateDateThatNotInFuture } from '@insurancemenu/imenu-components-lib/dist/helpers/validateDateOfBirth';
import { AlignmentTypes } from '@insurancemenu/imenu-components-lib/dist/types/Tooltip';
import { createStyles } from './styles';

export interface ProductLineDataProps {
  productLineData: InstalledProductLine;
  renewalAction: (productLineId: ProductLineId, action: ProductType) => void;
  defaultChoice: string | null;
  enrollmentPeriod: Pick<EnrollmentPeriod, 'installedProposal' | 'effectiveDate'>;
}

const RenewalPortfolioCard: React.FC<ProductLineDataProps> = observer(
  ({ productLineData, renewalAction, defaultChoice, enrollmentPeriod }) => {
    // Mobx store
    const {
      userStore: { userRoles },
      groupProposalsTableStore: { setShowPlanDetailsModal },
      browsingModalsStore: { setEffectiveDate },
      domainStoreV2: {
        isChangePlanActionAvailable,
        isRenewAsIsActionAvailable,
        isCancelCoverageActionAvailable,
        isRetroQuotingPeriodPassed,
      },
    } = useRootStore();

    const convertedUserRoles = userRoles.map((role) => toJS(role)).slice();

    const { id: productLineId, carrierPortfolioName, products } = productLineData;

    const [choice, setChoice] = useState(defaultChoice);

    const isSelectedAsRenew = choice === ProductType.RENEW;
    const isCancelled = choice === ProductType.CANCEL;
    const isPortfolioWithSeveralProducts = products.length > 1;
    const showRenewalCard = products.some((product) => {
      return !!product.rates.find(({ renewalRate }) => renewalRate);
    });

    // Check if effective date has passed (current date is after effective date)
    const isEffectiveDatePassed = enrollmentPeriod.effectiveDate
      ? validateDateThatNotInFuture(enrollmentPeriod.effectiveDate)
      : false;

    if (!showRenewalCard) return null;

    const classes = createStyles({
      productsLength: products.length,
      isSelectedAsRenew,
      isCancelled,
    });

    const exhibitUrls = enrollmentPeriod.installedProposal?.productLines.find(
      (line) => line.id === productLineId,
    )?.exhibitUrls;

    const downloadExhibit = async (): Promise<void> => {
      if (exhibitUrls) {
        const zipUrl = await getUploadExhibitZipUrl(exhibitUrls);
        if (zipUrl) {
          downloadByUrl(zipUrl);
        }
      }
    };

    const openProductDetailsModal = (): void => {
      setEffectiveDate(enrollmentPeriod.effectiveDate);
      setShowPlanDetailsModal(true);
    };

    const downloadButton = (
      <Button
        leftIconProp={<Download />}
        type="text"
        size="small"
        className={classes.downloadButton}
        color="primary"
        onClick={downloadExhibit}
        disabled={!exhibitUrls || exhibitUrls?.length === 0}
      >
        Download Renewal & Exhibits
      </Button>
    );

    const ratesItems = (imProductId: string, rates: InstalledRate[]): JSX.Element[] => {
      return rates
        .filter(({ renewalRate }) => renewalRate && renewalRate !== 'N/A')
        .map(({ rateStructure, renewalRate }) => {
          return (
            <li key={`${imProductId}-${rateStructure}`} className={classes.ratesItem}>
              <span>{rateStructure}</span>
              <span>{convertRateValue(renewalRate)}</span>
            </li>
          );
        });
    };

    const RenewalCardHeader = (
      <div className={classes.renewalProductHeader}>
        {isSelectedAsRenew && <p className={classes.renewalTag}>Selected: Renew As Is</p>}
        {isCancelled && <p className={classes.renewalTag}>Selected: Cancel Coverage</p>}
        {!isCancelled && !isSelectedAsRenew && <p className={classes.renewalTag}>Not Yet Selected</p>}
      </div>
    );
    const isAnyButtonDisabledDueDateValidation =
      isEffectiveDatePassed || isRetroQuotingPeriodPassed(enrollmentPeriod.effectiveDate);

    const RenewalButtons = (
      <div className={classes.renewalsButtons}>
        <div className={classes.addBenefitButtonWrapper}>
          <Button
            leftIconProp={<Add />}
            type="text"
            size="small"
            color="primary"
            disabled={isRetroQuotingPeriodPassed(enrollmentPeriod.effectiveDate)}
            onClick={openProductDetailsModal}
          >
            Add Benefit
          </Button>
          {isAnyButtonDisabledDueDateValidation && (
            <Tooltip alignment={AlignmentTypes.LEFT_CENTER} label="Actions may be disabled due to date validation">
              <InfoButton className={classes.infoButton} width="2rem" height="2rem" color={colors.red[600]} />
            </Tooltip>
          )}
        </div>
        {!employerRights(convertedUserRoles) && isChangePlanActionAvailable && (
          <Button
            leftIconProp={<Search />}
            type="text"
            size="small"
            color="primary"
            disabled={isEffectiveDatePassed}
            onClick={openProductDetailsModal}
          >
            Browse All
          </Button>
        )}
        {isRenewAsIsActionAvailable && (
          <Button
            leftIconProp={<Reload />}
            type="text"
            size="small"
            color="primary"
            className={classes.renewAsIsButton}
            disabled={isEffectiveDatePassed}
            onClick={(): void => {
              setChoice(ProductType.RENEW);
              renewalAction(productLineId, ProductType.RENEW);
            }}
          >
            Renew as is
          </Button>
        )}
        {isCancelCoverageActionAvailable && (
          <Button
            leftIconProp={<NavigationClose />}
            type="text"
            size="small"
            color="custom"
            customColor={colors.red[600]}
            disabled={isEffectiveDatePassed}
            onClick={(): void => {
              setChoice(ProductType.CANCEL);
              renewalAction(productLineId, ProductType.CANCEL);
            }}
          >
            Cancel coverage
          </Button>
        )}

        {isPortfolioWithSeveralProducts && downloadButton}
      </div>
    );

    const renewalProductsList = products.map(
      ({ carrierProductId, carrierProductName, renewalCarrierProductName, rates }) => {
        const productName = renewalCarrierProductName || carrierProductName;
        const filteredRates = rates.filter(({ renewalRate }) => renewalRate && renewalRate !== 'N/A');

        const RatesContent = (
          <div>
            {filteredRates.length > 0 ? (
              <ul className={classes.renewalRatesList}>{ratesItems(carrierProductId, rates)}</ul>
            ) : (
              <div className={classes.ratesMessage}>
                <InfoButton width="2rem" height="2rem" color={colors.red[600]} /> <span>Rates are unavailable</span>
              </div>
            )}
          </div>
        );

        if (isPortfolioWithSeveralProducts) {
          return (
            <div key={`${carrierProductId}_${productName}`} className={classes.multiProductWrapper}>
              <h3 className={classes.productTitle}>{productName}</h3>
              <div className={classes.multiProductRatesWrapper}>
                <p className={classes.ratesTitle}>Renewal Rates</p>
                {RatesContent}
              </div>
            </div>
          );
        }

        return (
          <div className={classes.renewalProductCard} key={`${carrierProductId}_${productName}`}>
            {RenewalCardHeader}
            <h3 className={clsx(classes.productTitle, classes.singleProductPortfolioTitle)}>{productName}</h3>
            <div className={classes.renewalCardContent}>
              <div className={classes.ratesWrapper}>
                <p className={classes.ratesTitle}>Renewal Rates</p>
                <ul className={classes.renewalRatesList}>{ratesItems(carrierProductId, rates)}</ul>
              </div>
              {RenewalButtons}
            </div>
            {downloadButton}
          </div>
        );
      },
    );

    return (
      <>
        {isPortfolioWithSeveralProducts ? (
          <div className={clsx(classes.renewalProductCard, classes.multiProductCard)}>
            {RenewalCardHeader}
            <div className={classes.multiProductHeader}>
              <h3 className={clsx(classes.productTitle, classes.multiProductPortfolioTitle)}>{carrierPortfolioName}</h3>
            </div>
            <div className={classes.productsList}>{renewalProductsList}</div>
            {RenewalButtons}
          </div>
        ) : (
          renewalProductsList
        )}
      </>
    );
  },
);

export default RenewalPortfolioCard;
